/**
 * BedPicker — selector de llits controlat (runbook 10, llits-1).
 *
 * Extret de `BedConfigEditor` perquè el faci servir també `/admin/rooms` (fitxa F1). Vista prèvia
 * + un +/− per tipus de llit. No desa res: `onChange` retorna la llista nova.
 */

import { cn } from '@/lib/utils';
import { BedIcons, BedIcon, type BedType } from './BedIcon';
import type { BedConfigItem } from '@/hooks/useBedConfigMutation';
import { itemsToBeds } from './bed-review';

export interface BedPickerProps {
  value: BedConfigItem[];
  onChange: (value: BedConfigItem[]) => void;
  disabled?: boolean;
}

interface BedTypeConfig {
  type: BedType;
  label: string;
  emoji: string;
}

const BED_TYPE_CONFIGS: BedTypeConfig[] = [
  { type: 'double', label: 'Matrimoni', emoji: '🛏️' },
  { type: 'single', label: 'Individual', emoji: '🛏' },
  { type: 'crib', label: 'Cuna', emoji: '🍼' },
  { type: 'extra', label: 'Supletori', emoji: '➕' },
  { type: 'bunk', label: 'Llitera', emoji: '🪜' },
];

function getQty(items: BedConfigItem[], type: BedType): number {
  return items.find((i) => i.type === type)?.qty ?? 0;
}

/** Actualitza la quantitat d'un tipus (l'elimina si qty = 0). */
function setQty(items: BedConfigItem[], type: BedType, qty: number): BedConfigItem[] {
  const filtered = items.filter((i) => i.type !== type);
  return qty > 0 ? [...filtered, { type, qty }] : filtered;
}

export function BedPicker({ value, onChange, disabled = false }: BedPickerProps) {
  const previewBeds = itemsToBeds(value);

  return (
    <div className="flex flex-col gap-2">
      {/* Preview en temps real */}
      {previewBeds.length > 0 && (
        <div className="flex items-center gap-1 pb-1 border-b border-gray-200 min-w-0 overflow-hidden">
          <span className="text-[10px] text-gray-400 flex-shrink-0">Vista prèvia:</span>
          <BedIcons beds={previewBeds} gap="sm" className="flex-wrap" />
        </div>
      )}

      {/* Controls per tipus de llit */}
      <div className="flex flex-col gap-1">
        {BED_TYPE_CONFIGS.map(({ type, label, emoji }) => {
          const qty = getQty(value, type);
          return (
            <div key={type} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <BedIcon type={type} />
                <span className="text-xs text-gray-700 truncate">
                  {emoji} {label}
                </span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  aria-label={`Treure ${label}`}
                  className={cn(
                    'w-6 h-6 rounded border text-sm font-bold leading-none transition-colors',
                    qty > 0
                      ? 'border-red-200 text-red-600 hover:bg-red-50'
                      : 'border-gray-200 text-gray-300 cursor-not-allowed',
                  )}
                  onClick={() => onChange(setQty(value, type, qty - 1))}
                  disabled={qty === 0 || disabled}
                >
                  −
                </button>
                <span
                  className="w-5 text-center text-sm font-semibold text-gray-800"
                  data-bed-qty={type}
                >
                  {qty}
                </span>
                <button
                  type="button"
                  aria-label={`Afegir ${label}`}
                  className="w-6 h-6 rounded border border-green-200 text-green-600 hover:bg-green-50 text-sm font-bold leading-none transition-colors"
                  onClick={() => onChange(setQty(value, type, qty + 1))}
                  disabled={disabled}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BedPicker;
